import { CreateShapeResponse, EditShapeResponse } from '@ais-ui/api';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  DrawGeometryType,
  shapeToVectorLayer,
  useDrawingContext,
  useDrawingLifecycle,
  useLayers,
  useOpenLayersContext,
  useResetDrawingContext,
  checkAntimeridianCrossingGeometry,
  AntimeridianDialog,
  LongitudeSpans,
} from '@ais-ui/react-openlayers';
import {
  Button,
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Label,
  LineColorPickerWithNames,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import { useGetShareGroups } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import {
  useFieldArray,
  useForm,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { GroupMultiSelect } from '../../Groups/GroupMultiSelect';
import { GroupSelect } from '../GroupSelect';
import { grabShapeInitialGroups } from '../../Groups/grabInitialGroups';
import { mutateCallbacks, shapeCallbacks } from '../../utils/mutateCallbacks';
import { UseMutateFunction } from '@tanstack/react-query';
import { useLocalization } from '@ais-ui/locale-management';
import { useOwnershipCheck } from '../../utils/useOwnershipCheck';
import { DrawPointField } from '../DrawFormFields/DrawPointField';
import {
  CircleGeometry,
  geometrySchema,
  getInitialGeometry,
} from '@ais-ui/utils';
import { calculateShapeArea } from '@ais-ui/utils';
import { DrawLineField } from '../DrawFormFields/DrawLineField';
import { DrawPolygonField } from '../DrawFormFields/DrawPolygonField';
import { DrawCircleField } from '../DrawFormFields/DrawCircleField';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { useDrawWithViewMode } from '../../../hooks/useDrawWithViewMode';
import { useViewMode, ViewMode } from '../../../components/ViewModeProvider';

interface SaveShapeCreateDialogProps {
  setShowOuterDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<CreateShapeResponse, unknown, GeoJson, unknown>;
  action: 'create';
  shape?: GeoJson;
  geometryType: DrawGeometryType;
  startInDrawMode: boolean;
}

interface SaveShapeEditDialogProps {
  setShowOuterDialog: React.Dispatch<React.SetStateAction<boolean>>;
  mutate: UseMutateFunction<
    EditShapeResponse,
    unknown,
    { id: string; json: GeoJson },
    unknown
  >;
  action: 'edit';
  shape: GeoJson;
  geometryType: DrawGeometryType;
  startInDrawMode: boolean;
}

type SaveShapeDialogProps =
  | SaveShapeCreateDialogProps
  | SaveShapeEditDialogProps;

const mutateShapeFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  geometry: geometrySchema,
  parentId: z.string(),
  selectedGroups: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    }),
  ),
  color: z.string(),
  keyValuePairs: z.array(z.object({ key: z.string(), value: z.any() })),
});

export type MutateShapeFormValues = z.infer<typeof mutateShapeFormSchema>;

interface ShapeFormProps {
  form: UseFormReturn<MutateShapeFormValues>;
  onStartDrawing: (
    geometryType: DrawGeometryType,
    shape?: GeoJson | null,
  ) => void;
  geometryType: DrawGeometryType;
  isOwner: boolean;
}

const SaveShapeForm = ({
  form,
  onStartDrawing,
  geometryType,
  isOwner,
}: ShapeFormProps) => {
  const { messages } = useLocalization();

  const geometry = form.watch('geometry');

  const colorWatch = useWatch({
    control: form.control,
    name: 'color',
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: 'keyValuePairs',
  });

  const parentId = form.watch('parentId');

  useEffect(() => {
    if (parentId) {
      form.setValue('selectedGroups', []);
    }
  }, [parentId, form]);

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeNameLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input id="shape-name" type="text" autoFocus {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="space-y-2">
        <Label>Geometry</Label>
        {geometryType === 'Point' && (
          <DrawPointField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawPointField>
        )}
        {geometryType === 'LineString' && (
          <DrawLineField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawLineField>
        )}
        {geometryType === 'Polygon' && (
          <DrawPolygonField
            key={colorWatch}
            form={form}
            geometry={geometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawPolygonField>
        )}
        {geometryType === 'Circle' && (
          <DrawCircleField
            key={colorWatch}
            form={form}
            geometry={geometry as CircleGeometry}
            onStartDrawing={onStartDrawing}
            color={colorWatch}
          ></DrawCircleField>
        )}
      </div>
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {messages.shapesCreateShapeDescriptionLabel}
              </FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Textarea id="shape-description" {...field} />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        key="color"
        control={form.control}
        name="color"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeColorLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeColorTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl
              onChange={() => {
                form.clearErrors();
              }}
            >
              <LineColorPickerWithNames
                background={field.value}
                setBackground={(color) => {
                  form.setValue('color', color);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="parentId"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeParentIdLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeParentIdTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupSelect value={field.value} onChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapesCreateShapeShareWithLabel}</FormLabel>
              <InfoIconTooltip>
                {messages.shapesCreateShapeShareWithTooltip}
              </InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                {...field}
                disabled={!!parentId || !isOwner}
                disabledReason={
                  !isOwner
                    ? messages.genericNotOwnerDisabledText ||
                      'You must be the owner to share this object'
                    : messages.shapeDisabledShareText
                }
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="keyValuePairs"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Metadata</FormLabel>
              <InfoIconTooltip>
                The metadata is used in the SQL report
              </InfoIconTooltip>
            </div>

            <FormControl onChange={() => form.clearErrors()}>
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div
                    key={`${field.id}`}
                    className="flex space-x-2 items-center"
                  >
                    <Button
                      type="button"
                      size="icon"
                      disabled={true}
                      variant="ghost"
                      className="h-9 w-9"
                      onClick={() => remove(index)}
                    >
                      {index + 1}
                    </Button>
                    <Input
                      id={`key-${index}`}
                      type="text"
                      placeholder="Enter key"
                      defaultValue={field.key}
                      onChange={(e) => {
                        form.setValue(
                          `keyValuePairs.${index}.key`,
                          e.target.value,
                        );
                        fields[index].key = e.target.value;
                      }}
                      className="w-64"
                    />
                    <Input
                      id={`value-${index}`}
                      type="text"
                      placeholder="Enter value"
                      defaultValue={field.value}
                      onChange={(e) => {
                        form.setValue(
                          `keyValuePairs.${index}.value`,
                          e.target.value,
                        );
                        fields[index].value = e.target.value;
                      }}
                      className="w-64"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="h-9 w-9"
                          onClick={() => remove(index)}
                        >
                          <PiMinusThin className="size-4"></PiMinusThin>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Remove key-value pair</TooltipContent>
                    </Tooltip>
                  </div>
                ))}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-9 w-9"
                      onClick={() =>
                        append({
                          key: '',
                          value: '',
                        })
                      }
                    >
                      <PiPlusThin className="size-4"></PiPlusThin>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Add key-value pair</TooltipContent>
                </Tooltip>
              </div>
            </FormControl>
            {form.formState.errors.keyValuePairs && (
              <p className="text-sm font-medium text-destructive">
                Something went wrong with key value pairs
              </p>
            )}
          </FormItem>
        )}
      />
    </>
  );
};

interface ShapeProperties {
  name: string;
  description: string;
  parentId: string;
  acl: string[];
  radius?: number; // Optional radius property
  [key: string]: any; // key value pairs
}

export function SaveShapeDialog({
  action,
  setShowOuterDialog,
  geometryType,
  shape,
  mutate,
  startInDrawMode,
}: SaveShapeDialogProps) {
  const { messages } = useLocalization();
  const { removeLayerById, addLayer, doesLayerExist } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const drawingContext = useDrawingContext();
  const resetDrawingContext = useResetDrawingContext();
  const { onAcceptDrawing } = useDrawingLifecycle();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const { isOwner } = useOwnershipCheck();

  const { toast } = useToast();

  const knownKeys = ['name', 'acl', 'description', 'parentId', 'id', 'radius'];

  const form = useForm<MutateShapeFormValues>({
    resolver: zodResolver(mutateShapeFormSchema),
    defaultValues: {
      name: shape?.properties.name || '',
      geometry: getInitialGeometry(shape, geometryType),
      description: shape?.properties.description || '',
      parentId: shape?.properties?.parentId || '',
      color: shape?.properties.color || '#003b5c',
      selectedGroups: grabShapeInitialGroups(shape, groups),
      keyValuePairs: Object.entries(shape?.properties || {})
        .filter(([key]) => !knownKeys.includes(key))
        .map(([key, value]) => ({ key, value })),
    },
  });

  const { setViewMode, viewMode } = useViewMode();

  // Wrap the onCancel to reset ViewMode before unmounting the component
  useEffect(() => {
    const originalOnCancel = drawingContext.onCancel.current;

    const onCancelDrawingWithDialogClose = () => {
      // Reset ViewMode BEFORE unmounting the component
      // This is necessary because the useEffect in useDrawWithViewMode won't run
      // if the component is unmounted before the state change propagates
      if (viewMode === ViewMode.Drawing) {
        setViewMode(ViewMode.Normal);
      }
      if (originalOnCancel) {
        originalOnCancel(); // Call the original onCancel function
      }
      setShowOuterDialog(false);
    };

    drawingContext.onCancel.current = onCancelDrawingWithDialogClose;
  }, [drawingContext, setShowOuterDialog, viewMode, setViewMode]);

  const [showDialog, setShowDialog] = useState<boolean>(false);

  // Antimeridian dialog state
  const [showAntimeridianDialog, setShowAntimeridianDialog] = useState(false);
  const [antimeridianSpans, setAntimeridianSpans] = useState<LongitudeSpans>({
    rawSpan: 0,
    normalizedSpan: 0,
    antimeridianSpan: 360,
  });

  const { showDrawPanel } = useDrawingContext();

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen); // Update showDialog state

    // Make sure that we are not unmounting the component when drawing, but we will unmount it if we are not drawing
    if (!showDrawPanel) {
      setShowOuterDialog(isOpen);
    }
  };

  const { onStartDrawingFromDialog } = useDrawWithViewMode(form, setShowDialog);

  // Watch geometry and check for antimeridian issues when it changes
  const geometry = form.watch('geometry');
  const [hasCheckedAntimeridian, setHasCheckedAntimeridian] = useState(false);

  useEffect(() => {
    // Only check once per geometry, for polygons and linestrings
    if (
      (geometry?.type === 'Polygon' || geometry?.type === 'LineString') &&
      !hasCheckedAntimeridian &&
      showDialog
    ) {
      const check = checkAntimeridianCrossingGeometry(
        geometry as { type: string; coordinates: number[][] | number[][][] },
      );

      if (check && check.mightCross) {
        setAntimeridianSpans(check.spans);
        setShowAntimeridianDialog(true);
        setHasCheckedAntimeridian(true);
      }
    }
  }, [geometry, hasCheckedAntimeridian, showDialog]);

  // Reset the check flag when starting a new drawing
  const handleStartDrawing = (
    geoType: DrawGeometryType,
    existingShape: GeoJson | undefined | null,
    maxPoints?: number,
  ) => {
    setHasCheckedAntimeridian(false);
    onStartDrawingFromDialog(geoType, existingShape, maxPoints);
  };

  // Handle antimeridian dialog choices
  const handleAntimeridianConfirm = () => {
    // User says YES it crosses - keep coordinates as-is, continue with form
    console.log('[Antimeridian] User confirmed: polygon crosses antimeridian');
    setShowAntimeridianDialog(false);
  };

  const handleAntimeridianRedraw = () => {
    // User says NO - go back to drawing mode
    console.log('[Antimeridian] User wants to redraw');
    setShowAntimeridianDialog(false);
    setShowDialog(false);
    setHasCheckedAntimeridian(false);
    // Start drawing again - the new geometry will replace the old one
    onStartDrawingFromDialog(geometryType, null);
  };

  // Start drawing when the dialog is opened
  useEffect(() => {
    if (startInDrawMode) {
      handleStartDrawing(geometryType, shape);
    } else {
      setShowDialog(true);
    }
  }, []);

  const [dialogTitle, dialogDescription] =
    action === 'edit'
      ? [messages.shapesEditShapeTitle, messages.shapesEditShapeDescription]
      : [
          messages.shapesCreateShapeTitle,
          messages.shapesCreateShapeDescription,
        ];

  const onSubmit = (data: MutateShapeFormValues) => {
    onAcceptDrawing();

    if (!data.geometry) {
      return;
    }

    let radius = null;
    if (data.geometry?.type === 'Point' && 'radius' in data.geometry) {
      radius = (data.geometry as CircleGeometry).radius;
    }

    const keyValueObject = data.keyValuePairs.reduce(
      (acc, pair) => {
        acc[pair.key] = pair.value;
        return acc;
      },
      {} as { [key: string]: any },
    );

    // Calculate area for polygon or circle shapes
    const areaResult = calculateShapeArea(data.geometry, radius ?? undefined);

    const properties: ShapeProperties = {
      name: data.name,
      description: data.description,
      parentId: data.parentId,
      acl: data.selectedGroups.map((group) => group.value),
      color: data.color,
      ...keyValueObject,
    };

    if (radius) {
      properties['radius'] = radius;
    }

    if (areaResult) {
      properties['area'] = areaResult.formatted;
    }

    const geoJson = {
      type: 'Feature',
      properties: properties,
      geometry: data.geometry,
    } as GeoJson;

    setIsSubmitting(true);

    const callbacks = shapeCallbacks(
      toast,
      messages,
      action,
      setIsSubmitting,
      setShowDialog,
    );
    const extendedOnSuccess = () => {
      callbacks.onSuccess();

      if (drawingContext.geometryType) {
        resetDrawingContext();
      }
      setShowOuterDialog(false);
    };

    const extendedCallbacks = {
      ...callbacks,
      onSuccess: extendedOnSuccess,
    };

    if (action === 'edit') {
      const shapeName = shape.properties.name;
      const id = shape?.properties.id;
      if (!id) {
        toast({
          className: 'save-shape-error-toast',
          title: 'Error!',
          description: `Failed to edit the shape ${shapeName}, missing id.`,
          variant: 'destructive',
        });
        setShowDialog(false);
        return;
      }
      geoJson.properties.id = id; // need to be set here otherwise it won't be shown on the map
      mutate({ id, json: geoJson }, extendedCallbacks);

      if (doesLayerExist(id)) {
        removeLayerById(id);
        const layer = shapeToVectorLayer(
          geoJson,
          displayProjection,
          dataProjection,
        );
        // Use the new name from the form data, not the old shape's name
        const layerName = geoJson.properties.name || 'No name';

        // Add type: 'shape' to properties for context menu detection
        const propertiesWithType = {
          ...geoJson.properties,
          type: 'shape',
        };

        addLayer(
          layer,
          layerName,
          true, // isRemovableFromLayerControl
          true, // canZoomToFromLayerControl
          true, // canShowLabel
          propertiesWithType,
          geoJson.properties.id,
          false, // showLabel
          false, // clickable
          false, // hoverEnabled
          'below-ships', // position
          true, // contextMenuEnabled
        );
      }
    } else {
      mutate(geoJson, extendedCallbacks);
    }
  };

  const handleExitConfirm = React.useCallback(() => {
    if (drawingContext.geometryType) {
      resetDrawingContext();
    }
    setShowDialog(false);
    setShowOuterDialog(false);
  }, [
    drawingContext.geometryType,
    resetDrawingContext,
    setShowDialog,
    setShowOuterDialog,
  ]);

  const { handleClose: handleExitWarning, ExitWarningDialog } =
    useDialogExitWarning({
      onConfirm: handleExitConfirm,
      title: messages.shapesWarningTitle || 'Discard shape?',
      description:
        messages.shapesWarningDescription ||
        'Your shape configuration will be lost. Are you sure you want to close?',
    });

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={handleOpenChange}
        title={dialogTitle}
        description={dialogDescription}
        isLoading={isLoadingGroups}
        hasLoaded={!isLoadingGroups && !!groups}
        buttonId="shape-confirm-save-button"
        buttonText={
          isSubmitting ? messages.genericSubmitting : messages.genericSubmit
        }
        buttonDisabled={isSubmitting}
        buttonOnClick={form.handleSubmit(onSubmit)}
        observeResize={false}
        onClose={handleExitWarning}
      >
        <SaveShapeForm
          form={form}
          onStartDrawing={handleStartDrawing}
          geometryType={geometryType}
          isOwner={action === 'create' || isOwner(shape?.properties)}
        />
      </DialogForm>
      <ExitWarningDialog />
      <AntimeridianDialog
        open={showAntimeridianDialog}
        spans={antimeridianSpans}
        onConfirmCrossing={handleAntimeridianConfirm}
        onRedraw={handleAntimeridianRedraw}
        geometryType={
          geometry?.type === 'LineString' ? 'LineString' : 'Polygon'
        }
      />
    </Form>
  );
}
